
import { RequestStatus } from 'common/enums/request-status';
import { Role } from 'common/interfaces/account/role';
import { MonoliteHelper } from 'common/monolite';
import { SubscriptionAndCompanyIdPayload } from 'unit-subscription/actions/payloads';
import { EstimateSubscriptionError } from 'unit-subscription/interfaces/estimate-subscription-error';
import { Account } from '../../../common/interfaces/account';
import { ReducerMethods } from '../../../common/interfaces/reducer-methods';
import { PaymentMethodActionTypes, SubscriptionActionTypes } from '../actions/types';
import { BillingCountryModel } from '../interfaces/billing-country-model';
import { CompanyBillingInfoModel } from '../interfaces/company-billing-info-model';
import { EstimateSubscriptionModel } from '../interfaces/estimation-subscription-model';
import { SubscriptionPlanListingModel } from '../interfaces/subscription-plan-listing-model';


const paymentRequest = (s: Account): Account => new MonoliteHelper(s)
  .set(_ => _.paymentStatus, RequestStatus.Loading)
  .get();
const paymentRequestFailed = (s: Account): Account => new MonoliteHelper(s)
  .set(_ => _.paymentStatus, RequestStatus.Failed)
  .get();
const paymentRequestSucceeded = (s: Account, payload): Account => new MonoliteHelper(s)
  .set(_ => _.paymentStatus, RequestStatus.Loaded)
  .set(_ => _.payment, payload)
  .get();

const clearBillingEstimation = (s: Account): Account => new MonoliteHelper(s)
  .set(_ => _.billingEstimationError, null)
  .set(_ => _.billingEstimation, null)
  .get();


export const subscriptionReducerMethods: ReducerMethods<Account> = {
  [SubscriptionActionTypes.SET_SUBSCRIPTION_TYPE_AND_COMPANY_ID]: (s, payload: SubscriptionAndCompanyIdPayload) => {
    const selectedCompany = s.companies && s.companies.find(x => x.id === payload.companyId);
    const selectedOwnCompany = s.ownedCompany && s.ownedCompany.id === payload.companyId
      ? s.ownedCompany
      : null;
    return new MonoliteHelper(s)
      .set(_ => _.selectedSubscriptionType, payload.subscriptionType)
      .set(_ => _.selectedCompany, selectedCompany || selectedOwnCompany)
      .get();
  },
  [SubscriptionActionTypes.LOAD_SUBSCRIPTION_ROLES_SUCCEEDED]: (s, payload: Role[]) => {
    return new MonoliteHelper(s)
      .set(_ => _.subscriptionRoles.roles, payload)
      .get();
  },
  [SubscriptionActionTypes.UPDATE_SUBSCRIPTION_PERMISSION_ROLES_REQUEST]: (s) => {
    return new MonoliteHelper(s)
      .set((_) => _.updateRolesPermissionStatus, RequestStatus.Loading)
      .get();
  },
  [SubscriptionActionTypes.UPDATE_SUBSCRIPTION_ROLES_REQUEST]: (s) => {
    return new MonoliteHelper(s)
      .set((_) => _.updateRolesStatus, RequestStatus.Loading)
      .get();
  },
  [SubscriptionActionTypes.UPDATE_SUBSCRIPTION_ROLES_SUCCEEDED]: (s) => {
    return new MonoliteHelper(s)
      .set((_) => _.updateRolesStatus, RequestStatus.Loading)
      .get();
  },
  [SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_REQUEST]: (s) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.requests.subscriptionPlans, RequestStatus.Loading);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_SUCCEEDED]: (s, model: SubscriptionPlanListingModel) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.subscriptionPlans, model)
      .set(_ => _.requests.subscriptionPlans, RequestStatus.Loaded);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.GET_SUBSCRIPTION_PLANS_FAILED]: (s) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.requests.subscriptionPlans, RequestStatus.Failed);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.GET_CREATE_SUBSCRIPTION_ESTIMATION]: clearBillingEstimation,
  [SubscriptionActionTypes.GET_UPDATE_SUBSCRIPTION_ESTIMATION]: clearBillingEstimation,
  [SubscriptionActionTypes.SET_SUBSCRIPTION_ESTIMATION_ERROR]: (s, payload: EstimateSubscriptionError) => {
    return new MonoliteHelper(s)
      .set(_ => _.billingEstimationError, payload)
      .get();
  },
  [SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_REQUEST]: (s) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.requests.ownCompanyBillingInfo, RequestStatus.Loading);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_FAILED]: (s) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.requests.ownCompanyBillingInfo, RequestStatus.Failed);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.FETCH_OWN_COMPANY_BILLING_INFO_SUCCEEDED]: (s, payload: CompanyBillingInfoModel) => {
    const stateUpdater = new MonoliteHelper(s);
    stateUpdater.set(_ => _.ownCompanyBillingInfo, { ...s.ownCompanyBillingInfo, ...payload })
      .set(_ => _.requests.ownCompanyBillingInfo, RequestStatus.Loaded);

    return stateUpdater.get();
  },
  [SubscriptionActionTypes.SET_SUBSCRIPTION_ESTIMATION]: (s, payload: EstimateSubscriptionModel) => {
    return new MonoliteHelper(s)
      .set(_ => _.billingEstimation, payload)
      .get();
  },
  [SubscriptionActionTypes.DROP_OWN_COMPANY_BILLING_INFO_STATE]: (s) => {
    return new MonoliteHelper(s)
      .set(_ => _.requests.ownCompanyBillingInfo, RequestStatus.NotRequested)
      .set(_ => _.ownCompanyBillingInfo, null)
      .set(_ => _.billingEstimation, null)
      .set(_ => _.billingEstimationError, null)
      .get();
  },
  [SubscriptionActionTypes.DROP_SUBSCRIPTION_PLANS_STATE]: (s) => {
    return new MonoliteHelper(s)
      .set(_ => _.requests.subscriptionPlans, RequestStatus.NotRequested)
      .set(_ => _.subscriptionPlans, null)
      .get();
  },

  [PaymentMethodActionTypes.SET_AVAILABLE_BILLING_COUNTRIES]: (s, payload: BillingCountryModel[]) => {
    return new MonoliteHelper(s)
      .set(_ => _.billingCountries, payload)
      .get();
  },

  [PaymentMethodActionTypes.SET_PRIMARY_CARD]: paymentRequest,
  [PaymentMethodActionTypes.SET_PRIMARY_CARD_FAILED]: paymentRequestFailed,
  [PaymentMethodActionTypes.SET_PRIMARY_CARD_SUCCEEDED]: paymentRequestSucceeded,

  [PaymentMethodActionTypes.SET_BACKUP_CARD]: paymentRequest,
  [PaymentMethodActionTypes.SET_BACKUP_CARD_FAILED]: paymentRequestFailed,
  [PaymentMethodActionTypes.SET_BACKUP_CARD_SUCCEEDED]: paymentRequestSucceeded,

  [PaymentMethodActionTypes.GET_PAYMENT_METHODS]: paymentRequest,
  [PaymentMethodActionTypes.GET_PAYMENT_METHODS_FAILED]: paymentRequestFailed,
  [PaymentMethodActionTypes.GET_PAYMENT_METHODS_SUCCEEDED]: paymentRequestSucceeded,

  [PaymentMethodActionTypes.REMOVE_CARD]: paymentRequest,
  [PaymentMethodActionTypes.REMOVE_CARD_FAILED]: paymentRequestFailed,
  [PaymentMethodActionTypes.REMOVE_CARD_SUCCEEDED]: paymentRequestSucceeded,

  [PaymentMethodActionTypes.CREATE_CARD]: paymentRequest,
  [PaymentMethodActionTypes.CREATE_CARD_FAILED]: paymentRequestFailed,
  [PaymentMethodActionTypes.CREATE_CARD_SUCCEEDED]: paymentRequestSucceeded,

  [PaymentMethodActionTypes.DROP_PAYMENT_STATE]: (s) => {
    return new MonoliteHelper(s)
      .set(_ => _.payment, null)
      .set(_ => _.paymentStatus, RequestStatus.NotRequested)
      .get();
  },
};
